import { useState } from "react";
import { mockProducts, mockCategories, mockSuppliers, mockShelves } from "@/mocks/dealerData";

type BulkPriceMode = "sale" | "purchase";
type BulkPriceType = "fixed" | "percent_increase" | "percent_decrease";

export default function AdminProductsPage() {
  const [products, setProducts] = useState(mockProducts.map((p) => ({ ...p, stock: Math.floor(Math.random() * 50) + 5 } as typeof mockProducts[0] & { stock: number })));
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<typeof products[0] | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string[] | null>(null);
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [bulkPriceMode, setBulkPriceMode] = useState<BulkPriceMode>("sale");
  const [bulkPriceType, setBulkPriceType] = useState<BulkPriceType>("fixed");
  const [bulkPriceValue, setBulkPriceValue] = useState(0);
  const [shelves, setShelves] = useState(mockShelves);
  const [categories, setCategories] = useState(mockCategories);
  const [showNewShelfInput, setShowNewShelfInput] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewBrandInput, setShowNewBrandInput] = useState(false);
  const [showNewModelInput, setShowNewModelInput] = useState(false);
  const [newShelfName, setNewShelfName] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newBrandName, setNewBrandName] = useState("");
  const [newModelName, setNewModelName] = useState("");
  const [form, setForm] = useState({
    name: "", productCode: "", barcode: "", brand: "", model: "",
    categoryId: "", supplierId: "", shelfLocation: "", purchasePrice: 0, salePrice: 0, imageUrl: "", stock: 0,
  });

  const filtered = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.productCode.toLowerCase().includes(search.toLowerCase()) ||
      p.barcode.includes(search) ||
      p.brand.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || p.categoryId === categoryFilter;
    const matchesSupplier = !supplierFilter || p.supplierId === supplierFilter;
    return matchesSearch && matchesCategory && matchesSupplier;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((p) => p.id)));
    }
  };

  const openAdd = () => {
    setEditingProduct(null);
    setForm({ name: "", productCode: "", barcode: "", brand: "", model: "", categoryId: "", supplierId: "", shelfLocation: "", purchasePrice: 0, salePrice: 0, imageUrl: "", stock: 0 });
    setShowNewShelfInput(false);
    setShowNewCategoryInput(false);
    setShowNewBrandInput(false);
    setShowNewModelInput(false);
    setNewShelfName("");
    setNewCategoryName("");
    setNewBrandName("");
    setNewModelName("");
    setShowAddModal(true);
  };

  const openEdit = (p: typeof products[0]) => {
    setEditingProduct(p);
    setForm({ name: p.name, productCode: p.productCode, barcode: p.barcode, brand: p.brand, model: p.model, categoryId: p.categoryId, supplierId: p.supplierId, shelfLocation: (p as any).shelfLocation || "", purchasePrice: p.purchasePrice, salePrice: p.salePrice, imageUrl: p.imageUrl, stock: p.stock });
    setShowNewShelfInput(false);
    setShowNewCategoryInput(false);
    setShowNewBrandInput(false);
    setShowNewModelInput(false);
    setNewShelfName("");
    setNewCategoryName("");
    setNewBrandName("");
    setNewModelName("");
    setShowAddModal(true);
  };

  const addNewShelf = () => {
    const trimmed = newShelfName.trim();
    if (!trimmed) return;
    const exists = shelves.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) return;
    const newShelf = { id: "sh" + Date.now(), name: trimmed };
    setShelves((prev) => [...prev, newShelf]);
    setForm({ ...form, shelfLocation: trimmed });
    setNewShelfName("");
    setShowNewShelfInput(false);
  };

  const addNewCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    const exists = categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) return;
    const newCat = { id: "c" + Date.now(), name: trimmed };
    setCategories((prev) => [...prev, newCat]);
    setForm({ ...form, categoryId: newCat.id });
    setNewCategoryName("");
    setShowNewCategoryInput(false);
  };

  const addNewBrand = () => {
    const trimmed = newBrandName.trim();
    if (!trimmed) return;
    setForm({ ...form, brand: trimmed });
    setNewBrandName("");
    setShowNewBrandInput(false);
  };

  const addNewModel = () => {
    const trimmed = newModelName.trim();
    if (!trimmed) return;
    setForm({ ...form, model: trimmed });
    setNewModelName("");
    setShowNewModelInput(false);
  };

  const saveProduct = () => {
    if (!form.name.trim()) return;
    if (editingProduct) {
      setProducts((prev) => prev.map((p) => (p.id === editingProduct.id ? { ...p, ...form } : p)));
    } else {
      setProducts((prev) => [...prev, { id: "p" + Date.now(), ...form }]);
    }
    setShowAddModal(false);
  };

  const confirmDelete = (ids: string[]) => {
    setDeleteConfirm(ids);
  };

  const executeDelete = () => {
    if (!deleteConfirm) return;
    setProducts((prev) => prev.filter((p) => !deleteConfirm.includes(p.id)));
    setSelectedIds(new Set());
    setDeleteConfirm(null);
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    confirmDelete([...selectedIds]);
  };

  const openBulkPrice = () => {
    setBulkPriceMode("sale");
    setBulkPriceType("fixed");
    setBulkPriceValue(0);
    setShowBulkPriceModal(true);
  };

  const applyBulkPrice = () => {
    if (selectedIds.size === 0 || bulkPriceValue <= 0) return;
    setProducts((prev) =>
      prev.map((p) => {
        if (!selectedIds.has(p.id)) return p;
        const currentPrice = bulkPriceMode === "sale" ? p.salePrice : p.purchasePrice;
        let newPrice: number;
        if (bulkPriceType === "fixed") {
          newPrice = bulkPriceValue;
        } else if (bulkPriceType === "percent_increase") {
          newPrice = currentPrice * (1 + bulkPriceValue / 100);
        } else {
          newPrice = currentPrice * (1 - bulkPriceValue / 100);
        }
        newPrice = Math.max(0, Math.round(newPrice * 100) / 100);
        if (bulkPriceMode === "sale") {
          return { ...p, salePrice: newPrice };
        } else {
          return { ...p, purchasePrice: newPrice };
        }
      })
    );
    setShowBulkPriceModal(false);
    setSelectedIds(new Set());
  };

  const selectedProducts = products.filter((p) => selectedIds.has(p.id));

  const uniqueBrands = [...new Set(products.map((p) => p.brand).filter(Boolean))].sort();
  const uniqueModels = [...new Set(products.map((p) => p.model).filter(Boolean))].sort();

  const statCategories = new Set(products.map((p) => p.categoryId));
  const totalValue = products.reduce((sum, p) => sum + p.salePrice * p.stock, 0);

  const bulkPriceTypeLabel = (type: BulkPriceType) => {
    if (type === "fixed") return "Sabit Fiyat";
    if (type === "percent_increase") return "Yüzde Artır";
    return "Yüzde Azalt";
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="admin-products-title">Ürün Yönetimi</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">{products.length} ürün, {statCategories.size} kategori, Toplam stok değeri: ${totalValue.toLocaleString("en-US")}</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <button onClick={openBulkPrice} className="flex items-center gap-2 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-medium hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-price-tag-3-line"></i> Toplu Fiyat ({selectedIds.size})
              </button>
              <button onClick={bulkDelete} className="flex items-center gap-2 px-4 py-2 bg-accent-100 text-accent-700 rounded-lg text-sm font-medium hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-delete-bin-line"></i> Seçili Sil ({selectedIds.size})
              </button>
            </>
          )}
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Ürün Ekle
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün adı, kod veya barkod ile ara..." className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400" />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer">
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} className="px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer">
          <option value="">Tüm Tedarikçiler</option>
          {mockSuppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[650px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                </th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ürün</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kod / Barkod</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Marka</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kategori</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Raf</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tedarikçi</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Alış</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Satış</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Stok</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} title={p.name} className="w-9 h-9 rounded-md object-cover shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-md bg-background-100 border border-background-200/70 flex items-center justify-center shrink-0"><i className="ri-image-line text-foreground-300 text-xs"></i></div>
                      )}
                      <div className="min-w-0">
                        <span className="font-medium text-foreground-900 block truncate">{p.name}</span>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">{categories.find((c) => c.id === p.categoryId)?.name || "-"}</span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">{p.brand}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className="text-foreground-900 text-xs font-mono">{p.productCode}</span>
                      <br />
                      <span className="text-foreground-400 text-xs">{p.barcode}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-600">{p.brand}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">{categories.find((c) => c.id === p.categoryId)?.name || "-"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-600 text-xs">{(p as any).shelfLocation || "-"}</span>
                  </td>
                  <td className="px-4 py-3 text-foreground-600">{mockSuppliers.find((s) => s.id === p.supplierId)?.name || "-"}</td>
                  <td className="px-4 py-3 text-foreground-700 text-right font-medium">${p.purchasePrice.toLocaleString("en-US")}</td>
                  <td className="px-4 py-3 text-primary-600 text-right font-medium">${p.salePrice.toLocaleString("en-US")}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.stock <= 3 ? "bg-accent-100 text-accent-700" : p.stock <= 10 ? "bg-secondary-100 text-secondary-700" : "bg-background-100 text-foreground-600"}`}>{p.stock}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(p)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Düzenle"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => confirmDelete([p.id])} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer" title="Sil"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-background-200/70">
          {filtered.map((p) => (
            <div key={p.id} className="p-4 hover:bg-background-100/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer shrink-0" />
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt={p.name} title={p.name} className="w-10 h-10 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-background-100 border border-background-200/70 flex items-center justify-center shrink-0"><i className="ri-image-line text-foreground-300 text-xs"></i></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground-900 text-sm truncate">{p.name}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">{categories.find((c) => c.id === p.categoryId)?.name || "-"}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">{p.brand}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs ml-[52px]">
                <div><span className="text-foreground-400">Kod:</span> <span className="text-foreground-700 font-mono">{p.productCode}</span></div>
                <div><span className="text-foreground-400">Barkod:</span> <span className="text-foreground-700">{p.barcode}</span></div>
                <div><span className="text-foreground-400">Raf:</span> <span className="text-foreground-700">{(p as any).shelfLocation || "-"}</span></div>
                <div><span className="text-foreground-400">Tedarikçi:</span> <span className="text-foreground-700">{mockSuppliers.find((s) => s.id === p.supplierId)?.name || "-"}</span></div>
                <div><span className="text-foreground-400">Alış:</span> <span className="text-foreground-700 font-medium">${p.purchasePrice.toLocaleString("en-US")}</span></div>
                <div><span className="text-foreground-400">Satış:</span> <span className="text-primary-600 font-medium">${p.salePrice.toLocaleString("en-US")}</span></div>
              </div>
              <div className="flex items-center justify-between mt-3 ml-[52px]">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.stock <= 3 ? "bg-accent-100 text-accent-700" : p.stock <= 10 ? "bg-secondary-100 text-secondary-700" : "bg-background-100 text-foreground-600"}`}>Stok: {p.stock}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer active:scale-[0.98]"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => confirmDelete([p.id])} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer active:scale-[0.98]"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-foreground-400">
            <i className="ri-box-3-line text-3xl block mb-2"></i>
            <p className="text-sm">Ürün bulunamadı</p>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAddModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="admin-product-form">{editingProduct ? "Ürün Düzenle" : "Yeni Ürün"}</a></h4>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Kodu</label><input type="text" value={form.productCode} onChange={(e) => setForm({ ...form, productCode: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Barkod</label><input type="text" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Marka</label><div className="flex items-center gap-1.5 mb-1"><button type="button" onClick={() => setShowNewBrandInput(!showNewBrandInput)} className="flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-700 rounded-md text-[11px] font-medium hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line text-xs"></i> Yeni Marka Ekle</button></div><select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"><option value="">Seçiniz veya yazın</option>{uniqueBrands.map((b) => (<option key={b} value={b}>{b}</option>))}</select>{showNewBrandInput && (<div className="flex items-center gap-2 mt-2"><input type="text" value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNewBrand(); }} placeholder="Marka adı yazın..." className="flex-1 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /><button type="button" onClick={addNewBrand} disabled={!newBrandName.trim()} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${!newBrandName.trim() ? "bg-background-200 text-foreground-400 cursor-not-allowed" : "bg-primary-500 hover:bg-primary-600 text-background-50"}`}>Ekle</button></div>)}</div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Model</label><div className="flex items-center gap-1.5 mb-1"><button type="button" onClick={() => setShowNewModelInput(!showNewModelInput)} className="flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-700 rounded-md text-[11px] font-medium hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line text-xs"></i> Yeni Model Ekle</button></div><select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"><option value="">Seçiniz veya yazın</option>{uniqueModels.map((m) => (<option key={m} value={m}>{m}</option>))}</select>{showNewModelInput && (<div className="flex items-center gap-2 mt-2"><input type="text" value={newModelName} onChange={(e) => setNewModelName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNewModel(); }} placeholder="Model adı yazın..." className="flex-1 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /><button type="button" onClick={addNewModel} disabled={!newModelName.trim()} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${!newModelName.trim() ? "bg-background-200 text-foreground-400 cursor-not-allowed" : "bg-primary-500 hover:bg-primary-600 text-background-50"}`}>Ekle</button></div>)}</div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Raf Yeri</label><div className="flex items-center gap-1.5 mb-1"><button type="button" onClick={() => setShowNewShelfInput(!showNewShelfInput)} className="flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-700 rounded-md text-[11px] font-medium hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line text-xs"></i> Yeni Raf Ekle</button></div><select value={form.shelfLocation} onChange={(e) => setForm({ ...form, shelfLocation: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"><option value="">Seçiniz</option>{shelves.map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}</select>{showNewShelfInput && (<div className="flex items-center gap-2 mt-2"><input type="text" value={newShelfName} onChange={(e) => setNewShelfName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNewShelf(); }} placeholder="Raf adı yazın..." className="flex-1 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /><button type="button" onClick={addNewShelf} disabled={!newShelfName.trim()} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${!newShelfName.trim() ? "bg-background-200 text-foreground-400 cursor-not-allowed" : "bg-primary-500 hover:bg-primary-600 text-background-50"}`}>Ekle</button></div>)}</div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Kategori</label><div className="flex items-center gap-1.5 mb-1"><button type="button" onClick={() => setShowNewCategoryInput(!showNewCategoryInput)} className="flex items-center gap-1 px-2 py-1 bg-accent-100 text-accent-700 rounded-md text-[11px] font-medium hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line text-xs"></i> Yeni Kategori Ekle</button></div><select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"><option value="">Seçiniz</option>{categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}</select>{showNewCategoryInput && (<div className="flex items-center gap-2 mt-2"><input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNewCategory(); }} placeholder="Kategori adı yazın..." className="flex-1 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /><button type="button" onClick={addNewCategory} disabled={!newCategoryName.trim()} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${!newCategoryName.trim() ? "bg-background-200 text-foreground-400 cursor-not-allowed" : "bg-primary-500 hover:bg-primary-600 text-background-50"}`}>Ekle</button></div>)}</div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Tedarikçi</label><select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"><option value="">Seçiniz</option>{mockSuppliers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}</select></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Alış Fiyatı ($)</label><input type="number" value={form.purchasePrice} onChange={(e) => setForm({ ...form, purchasePrice: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Satış Fiyatı ($)</label><input type="number" value={form.salePrice} onChange={(e) => setForm({ ...form, salePrice: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Stok Adedi</label><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div className="col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1">Görsel URL</label><input type="text" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={saveProduct} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-error-warning-line text-accent-600 text-xl"></i>
              </div>
              <h4 className="text-base font-semibold text-foreground-950"><a id="delete-confirm">Silme Onayı</a></h4>
              <p className="text-sm text-foreground-500 mt-1">{deleteConfirm.length} ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={executeDelete} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Sil</button>
            </div>
          </div>
        </div>
      )}

      {showBulkPriceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowBulkPriceModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-base font-semibold text-foreground-950"><a id="bulk-price-title">Toplu Fiyat Güncelleme</a></h4>
                <p className="text-xs text-foreground-500 mt-0.5">{selectedIds.size} ürün seçili</p>
              </div>
              <button onClick={() => setShowBulkPriceModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>

            {/* Fiyat türü seçimi: Satış / Alış */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-2">Fiyat Türü</label>
              <div className="flex gap-1 bg-background-100 rounded-lg p-1">
                <button
                  onClick={() => setBulkPriceMode("sale")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${bulkPriceMode === "sale" ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}
                >
                  Satış Fiyatı
                </button>
                <button
                  onClick={() => setBulkPriceMode("purchase")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${bulkPriceMode === "purchase" ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}
                >
                  Alış Fiyatı
                </button>
              </div>
            </div>

            {/* Güncelleme tipi: Sabit / Yüzde Artır / Yüzde Azalt */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-2">Güncelleme Tipi</label>
              <div className="grid grid-cols-3 gap-1 bg-background-100 rounded-lg p-1">
                {(["fixed", "percent_increase", "percent_decrease"] as BulkPriceType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setBulkPriceType(type)}
                    className={`py-2 px-2 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${bulkPriceType === type ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}
                  >
                    {bulkPriceTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>

            {/* Değer girişi */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-foreground-500 mb-1">
                {bulkPriceType === "fixed" ? "Yeni Fiyat ($)" : `${bulkPriceType === "percent_increase" ? "Artış" : "İndirim"} Oranı (%)`}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={bulkPriceValue}
                  onChange={(e) => setBulkPriceValue(Number(e.target.value))}
                  min={0}
                  step={bulkPriceType === "fixed" ? 0.01 : 1}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder={bulkPriceType === "fixed" ? "0.00" : "0"}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400">
                  {bulkPriceType === "fixed" ? "$" : "%"}
                </span>
              </div>
            </div>

            {/* Seçili ürünler önizleme */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-foreground-500 mb-2">Etkilenecek Ürünler</label>
              <div className="bg-background-100 rounded-lg border border-background-200/70 max-h-40 overflow-y-auto">
                {selectedProducts.map((p) => {
                  const currentPrice = bulkPriceMode === "sale" ? p.salePrice : p.purchasePrice;
                  let newPrice: number;
                  if (bulkPriceType === "fixed") {
                    newPrice = bulkPriceValue;
                  } else if (bulkPriceType === "percent_increase") {
                    newPrice = currentPrice * (1 + bulkPriceValue / 100);
                  } else {
                    newPrice = currentPrice * (1 - bulkPriceValue / 100);
                  }
                  newPrice = Math.max(0, Math.round(newPrice * 100) / 100);
                  const priceChanged = newPrice !== currentPrice && bulkPriceValue > 0;

                  return (
                    <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-background-200/70 last:border-b-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-foreground-900 truncate">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-foreground-500">${currentPrice.toLocaleString("en-US")}</span>
                        {priceChanged && (
                          <>
                            <i className="ri-arrow-right-line text-foreground-300 text-xs"></i>
                            <span className={`text-xs font-medium ${bulkPriceType === "percent_decrease" ? "text-accent-600" : "text-primary-600"}`}>
                              ${newPrice.toLocaleString("en-US")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowBulkPriceModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button
                onClick={applyBulkPrice}
                disabled={bulkPriceValue <= 0}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${bulkPriceValue <= 0 ? "bg-background-200 text-foreground-400 cursor-not-allowed" : "bg-primary-500 hover:bg-primary-600 text-background-50"}`}
              >
                {selectedIds.size} Ürüne Uygula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}